/**
 * Single config schema and validation. No scattered defaults.
 * @see docs/architecture/GREENFIELD-LECTURA-ARCHITECTURE.md
 */

import type { LecturaConfig, LecturaConfigFeatures, LecturaConfigRemote } from './types';

const DEFAULTS: LecturaConfig = Object.freeze({
  presentationId: 'deck',
  slidesVersion: '',
  showFab: true,
  fabLabel: 'Chat',
  chat: Object.freeze({
    firebaseConfig: {
      apiKey: 'AIzaSyAZ3P-ZC3BKdmwvLDixvtPig3woQYX3U8k',
      authDomain: 'founderstable-firebase.firebaseapp.com',
      projectId: 'founderstable-firebase',
      storageBucket: 'founderstable-firebase.firebasestorage.app',
      messagingSenderId: '358975217533',
      appId: '1:358975217533:web:12f3f0648dbc6ba78a3614',
      measurementId: 'G-98NRMDENYY'
    },
    mqtt: {
      url: 'wss://broker.hivemq.com:8884/mqtt'
    }
  }),
  formEndpoint: '',
  ratingLabel: 'Rate this presentation',
  showRatingAfterSlide: 'last',
  ratingFormEndpoint: '',
  confettiOnRate: true,
  features: Object.freeze({
    chat: true,
    notes: true,
    rating: true,
    auth: false,
    profile: true,
    tts: true,
    remote: true,
    resources: true
  } as LecturaConfigFeatures)
});

/** When features are not provided, infer from other config keys (e.g. chat, ratingFormEndpoint, chat.firebaseConfig). */
function inferFeatures(user: Partial<LecturaConfig>): LecturaConfigFeatures {
  const hasChat = !!user.chat && typeof user.chat === 'object';
  const hasFirebase = !!(user.firebaseConfig ?? (hasChat && (user.chat as { firebaseConfig?: unknown })?.firebaseConfig));
  return {
    chat: hasChat || DEFAULTS.features.chat,
    notes: true,
    rating: true,
    auth: hasFirebase,
    profile: true,
    tts: true,
    remote: true,
    resources: true
  };
}

/**
 * Merge user options with defaults. Result is frozen.
 * Config can come from __LECTURA_NEXT_CONFIG__ or lecturaConfig (same object pages use for plugin/lectura); Firebase is read from config.firebaseConfig or config.chat.firebaseConfig.
 */
export function getConfig(user: Partial<LecturaConfig> = {}): LecturaConfig {
  const viewerShell = user.__lecturaRemoteViewerShell === true;
  const explicitFeatures = user.features && typeof user.features === 'object' && Object.keys(user.features).length > 0;
  let features = explicitFeatures
    ? { ...DEFAULTS.features, ...user.features } as LecturaConfigFeatures
    : inferFeatures(user);
  if (viewerShell) {
    features = { ...features, remote: false };
  }
  const userSansShell = { ...(user as Record<string, unknown>) };
  delete userSansShell.__lecturaRemoteViewerShell;

  const hasV1Remote =
    features.remote ||
    !!(user.remote && user.remote.v1 && typeof user.remote.v1 === 'object') ||
    viewerShell;
  let remoteMerged: LecturaConfigRemote | undefined;
  if (hasV1Remote) {
    const base =
      user.remote && typeof user.remote === 'object' ? { ...user.remote } : ({} as LecturaConfigRemote);
    const v1In = base.v1;
    /** In-browser session store for demos/tests only; production uses HTTP `GET/POST /api/sessions/...`. */
    const devMock = v1In?.devMock === true;
    remoteMerged = {
      ...base,
      v1: {
        // Single source of truth: runtime registration uses `features.remote`.
        // Keep this field for compatibility (types/docs). Viewer shell still needs v1 session API even
        // when `features.remote` is forced off to avoid presenter registration in the same tab.
        enabled: features.remote || viewerShell,
        devMock,
        apiBaseUrl: v1In?.apiBaseUrl,
        getAuthHeaders: v1In?.getAuthHeaders
      }
    };
  } else if (user.remote && typeof user.remote === 'object') {
    remoteMerged = { ...user.remote };
  }

  const merged: LecturaConfig = {
    ...DEFAULTS,
    ...(userSansShell as Partial<LecturaConfig>),
    features,
    remote: remoteMerged,
    /** Kept on the frozen config so drawer-only `/remote/` can enable Speaker notes tab (not inferrable from features alone). */
    ...(viewerShell ? { __lecturaRemoteViewerShell: true as const } : {})
  };
  return Object.freeze(merged);
}

/**
 * Validate required fields. Throws if invalid.
 */
export function validateConfig(config: unknown): asserts config is LecturaConfig {
  if (!config || typeof config !== 'object') throw new Error('Lectura: config must be an object');
  const c = config as { presentationId?: string };
  if (typeof c.presentationId !== 'string') throw new Error('Lectura: config.presentationId must be a string');
}
